import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "16px 0",
  },
  accordion: {
    marginBottom: "12px",
    borderRadius: "12px !important",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1) !important",
    border: "1px solid #e0e0e0 !important",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: "0 0 12px 0 !important",
    },
  },
  accordionSummary: {
    backgroundColor: "#f8f9fa",
    borderRadius: "12px 12px 0 0",
    minHeight: "64px",
    "&.Mui-expanded": {
      minHeight: "64px",
      borderRadius: "12px 12px 0 0",
    },
  },
  leagueTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.primary.main,
  },
  leagueBadge: {
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "white",
  },
  formControl: {
    minWidth: "100%",
    marginBottom: "20px",
  },
  positionSelector: {
    marginBottom: "24px",
  },
  selectorTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
    fontWeight: 600,
  },
  selectionCard: {
    padding: "16px",
    marginBottom: "16px",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
  },
  selectedChip: {
    margin: "2px",
    fontWeight: 600,
  },
  promotionChip: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  demotionChip: {
    backgroundColor: "#ffebee",
    color: "#c62828",
  },
  rewardPackCard: {
    marginBottom: "24px",
    padding: "20px",
    backgroundColor: "#f0f7ff",
    border: "1px solid #bbdefb",
    borderRadius: "8px",
  },
  configSummary: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
  },
  helpText: {
    marginTop: "20px",
    padding: "16px",
    backgroundColor: "#f0f7ff",
    borderRadius: "8px",
    border: "1px solid #bbdefb",
  },
}));

const LEAGUE_TIERS = [
  { name: "STARTER", color: "#4caf50", order: 1 },
  { name: "BRONZE", color: "#8d6e63", order: 2 },
  { name: "BRONZE_II", color: "#795548", order: 3 },
  { name: "SILVER", color: "#9e9e9e", order: 4 },
  { name: "SILVER_II", color: "#607d8b", order: 5 },
  { name: "GOLD", color: "#ffc107", order: 6 },
  { name: "GOLD_II", color: "#ff9800", order: 7 },
  { name: "DIAMOND", color: "#2196f3", order: 8 },
  { name: "DIAMOND_II", color: "#3f51b5", order: 9 },
  { name: "PLATINUM", color: "#9c27b0", order: 10 },
];

const LeagueConfigurationEditor = ({
  data,
  onChange,
  rewardPackOptions,
  errors,
}) => {
  const styles = useStyles();
  const [expandedLeague, setExpandedLeague] = useState("STARTER");

  // Helper function to find a league by name in the array
  const findLeagueByName = (leagueName: any) => {
    return data.find((item) => item.name === leagueName);
  };

  // Helper function to update a specific league in the array
  const updateLeagueInArray = (leagueName, updatedLeagueConfig) => {
    const newData = data.map((item) => {
      if (item.name === leagueName) {
        return {
          ...item,
          leagueConfig: {
            ...item.leagueConfig,
            ...updatedLeagueConfig,
          },
        };
      }
      return item;
    });
    onChange(newData);
  };

  // Helper function to ensure all leagues exist in the array
  const ensureAllLeaguesExist = () => {
    if (!Array.isArray(data)) {
      // Initialize with all leagues if data is not an array
      const allLeagues = LEAGUE_TIERS.map((tier) => ({
        name: tier.name,
        order: tier.order,
        leagueConfig: {
          promotionPositions: [],
          demotionPositions: [],
          rewards: [
            {
              rewardPack: "",
              rewardPackCount: 1,
            },
          ],
        },
      }));
      onChange(allLeagues);
      return;
    }

    const existingLeagueNames = data.map((item) => item.name);
    const missingLeagues = LEAGUE_TIERS.filter(
      (tier) => !existingLeagueNames.includes(tier.name)
    );

    if (missingLeagues.length > 0) {
      const newLeagues = missingLeagues.map((tier) => ({
        name: tier.name,
        order: tier.order,
        leagueConfig: {
          promotionPositions: [],
          demotionPositions: [],
          rewards: [
            {
              rewardPack: "",
              rewardPackCount: 1,
            },
          ],
        },
      }));

      const updatedData = [...data, ...newLeagues].sort(
        (a, b) => a.order - b.order
      );
      onChange(updatedData);
    }
  };

  // Ensure all leagues exist when component mounts or data changes
  useEffect(() => {
    ensureAllLeaguesExist();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRewardPackChange = (leagueName, rewardIndex, field, value) => {
    const leagueItem = findLeagueByName(leagueName);
    if (!leagueItem) return;

    const updatedRewards =
      leagueItem.leagueConfig.rewards?.map((reward, index) =>
        index === rewardIndex ? { ...reward, [field]: value } : reward
      ) || [];

    updateLeagueInArray(leagueName, { rewards: updatedRewards });
  };

  const handleAddReward = (leagueName: any) => {
    const leagueItem = findLeagueByName(leagueName);
    if (!leagueItem) return;

    const currentRewards = leagueItem.leagueConfig.rewards || [];
    const updatedRewards = [
      ...currentRewards,
      {
        rewardPack: "",
        rewardPackCount: 1,
      },
    ];

    updateLeagueInArray(leagueName, { rewards: updatedRewards });
  };

  const handleRemoveReward = (leagueName, rewardIndex) => {
    const leagueItem = findLeagueByName(leagueName);
    if (!leagueItem) return;

    const currentRewards = leagueItem.leagueConfig.rewards || [];
    if (currentRewards.length <= 1) {
      return;
    }

    const updatedRewards = currentRewards.filter(
      (_, index) => index !== rewardIndex
    );
    updateLeagueInArray(leagueName, { rewards: updatedRewards });
  };

  const handlePromotionChange = (leagueName, selection) => {
    if (selection === "") {
      updateLeagueInArray(leagueName, { promotionPositions: [] });
      return;
    }

    const topCount = parseInt(selection.replace("TOP", ""));
    const positions = Array.from({ length: topCount }, (_, i) => i + 1);

    const leagueItem = findLeagueByName(leagueName);
    const currentDemotionPositions =
      leagueItem?.leagueConfig?.demotionPositions || [];
    const hasOverlap = positions.some((pos) =>
      currentDemotionPositions.includes(pos)
    );

    if (hasOverlap) {
      const conflicts = positions.filter((pos) =>
        currentDemotionPositions.includes(pos)
      );
      alert(
        `Cannot select promotion positions. Positions ${conflicts.join(
          ", "
        )} are already selected for demotion. Please adjust demotion positions first.`
      );
      return;
    }

    updateLeagueInArray(leagueName, { promotionPositions: positions });
  };

  const handleDemotionChange = (leagueName, selection) => {
    if (selection === "") {
      updateLeagueInArray(leagueName, { demotionPositions: [] });
      return;
    }

    const bottomCount = parseInt(selection.replace("BOTTOM", ""));
    const positions = Array.from(
      { length: bottomCount },
      (_, i) => 21 - bottomCount + i
    );

    const leagueItem = findLeagueByName(leagueName);
    const currentPromotionPositions =
      leagueItem?.leagueConfig?.promotionPositions || [];
    const hasOverlap = positions.some((pos) =>
      currentPromotionPositions.includes(pos)
    );

    if (hasOverlap) {
      const conflicts = positions.filter((pos) =>
        currentPromotionPositions.includes(pos)
      );
      alert(
        `Cannot select demotion positions. Positions ${conflicts.join(
          ", "
        )} are already selected for promotion. Please adjust promotion positions first.`
      );
      return;
    }

    updateLeagueInArray(leagueName, { demotionPositions: positions });
  };

  const getCurrentPromotionSelection = (leagueName: any) => {
    const leagueItem = findLeagueByName(leagueName);
    const league = leagueItem?.leagueConfig || {};
    const positions = league.promotionPositions || [];
    if (positions.length === 0) return "";

    const sortedPositions = [...positions].sort((a, b) => a - b);
    const isConsecutiveFromOne = sortedPositions.every(
      (pos, idx) => pos === idx + 1
    );

    if (isConsecutiveFromOne && sortedPositions[0] === 1) {
      return `TOP${positions.length}`;
    }
    return "CUSTOM";
  };

  const getCurrentDemotionSelection = (leagueName: any) => {
    const leagueItem = findLeagueByName(leagueName);
    const league = leagueItem?.leagueConfig || {};
    const positions = league.demotionPositions || [];
    if (positions.length === 0) return "";

    const sortedPositions = [...positions].sort((a, b) => b - a);
    const isConsecutiveFromEnd = sortedPositions.every(
      (pos, idx) => pos === 20 - idx
    );

    if (isConsecutiveFromEnd && sortedPositions[0] === 20) {
      return `BOTTOM${positions.length}`;
    }
    return "CUSTOM";
  };

  const renderPositionSelector = (leagueName, type) => {
    const isPromotion = type === "promotion";
    const currentSelection = isPromotion
      ? getCurrentPromotionSelection(leagueName)
      : getCurrentDemotionSelection(leagueName);

    const handleChange = isPromotion
      ? handlePromotionChange
      : handleDemotionChange;

    const leagueItem = findLeagueByName(leagueName);
    const league = leagueItem?.leagueConfig || {};
    const positions = isPromotion
      ? league.promotionPositions || []
      : league.demotionPositions || [];

    const conflictingPositions = isPromotion
      ? league.demotionPositions || []
      : league.promotionPositions || [];

    const wouldCauseConflict = (num: any) => {
      if (isPromotion) {
        const proposedPositions = Array.from({ length: num }, (_, i) => i + 1);
        return proposedPositions.some((pos) =>
          conflictingPositions.includes(pos)
        );
      } else {
        const proposedPositions = Array.from(
          { length: num },
          (_, i) => 21 - num + i
        );
        return proposedPositions.some((pos) =>
          conflictingPositions.includes(pos)
        );
      }
    };

    return (
      <Box className={styles.positionSelector}>
        <Typography className={styles.selectorTitle} variant="subtitle1">
          {isPromotion ? (
            <>
              <TrendingUpIcon color="success" />
              Promotion Configuration
            </>
          ) : (
            <>
              <TrendingDownIcon color="error" />
              Demotion Configuration
            </>
          )}
        </Typography>

        <FormControl size="small" fullWidth>
          <InputLabel>
            {isPromotion ? "Select TOP positions" : "Select BOTTOM positions"}
          </InputLabel>
          <Select
            value={currentSelection}
            onChange={(e) => handleChange(leagueName, e.target.value)}
            label={
              isPromotion ? "Select TOP positions" : "Select BOTTOM positions"
            }
          >
            <MenuItem value="">
              <em>No {isPromotion ? "promotion" : "demotion"}</em>
            </MenuItem>
            {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => {
              const isConflicting = wouldCauseConflict(num);
              return (
                <MenuItem
                  key={num}
                  value={`${isPromotion ? "TOP" : "BOTTOM"}${num}`}
                  disabled={isConflicting}
                  sx={{
                    opacity: isConflicting ? 0.5 : 1,
                    color: isConflicting ? "text.disabled" : "text.primary",
                  }}
                >
                  {isPromotion ? `TOP ${num}` : `BOTTOM ${num}`}
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 1,
                      color: isConflicting ? "error.main" : "textSecondary",
                    }}
                  >
                    (Positions {isPromotion ? `1-${num}` : `${21 - num}-20`})
                    {isConflicting && " - Conflicts!"}
                  </Typography>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {conflictingPositions.length > 0 && (
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              ⚠️ Note: Some positions are unavailable due to conflicts with{" "}
              {isPromotion ? "demotion" : "promotion"} settings
            </Typography>
          </Box>
        )}

        {positions.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Selected positions:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {positions.map((pos) => (
                <Chip
                  key={pos}
                  label={`#${pos}`}
                  size="small"
                  className={`${styles.selectedChip} ${
                    isPromotion ? styles.promotionChip : styles.demotionChip
                  }`}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Sort leagues by tier order
  const sortedLeagues = LEAGUE_TIERS.sort((a, b) => a.order - b.order);

  return (
    <Box className={styles.container}>
      {sortedLeagues.map((leagueTier) => {
        const leagueName = leagueTier.name;
        const leagueItem = findLeagueByName(leagueName);
        const league = leagueItem?.leagueConfig || {};
        const hasRewards =
          league.rewards &&
          league.rewards.length > 0 &&
          league.rewards.some((r) => r.rewardPack);

        return (
          <Accordion
            key={leagueName}
            className={styles.accordion}
            expanded={expandedLeague === leagueName}
            onChange={(event, isExpanded) =>
              setExpandedLeague(isExpanded ? leagueName : '' as any)
            }
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={styles.accordionSummary}
            >
              <Box display="flex" alignItems="center" width="100%" gap={2}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: leagueTier.color,
                  }}
                />
                <Typography className={styles.leagueTitle}>
                  {leagueName.replace(/_/g, " ")}
                </Typography>

                {/* Display order number */}
                <Chip
                  label={`Order: ${leagueItem?.order || leagueTier.order}`}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: "22px",
                    fontWeight: 600,
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    border: "1px solid #bbdefb",
                  }}
                />

                <Box className={styles.configSummary} ml="auto">
                  {league.promotionPositions?.length > 0 && (
                    <Chip
                      icon={<TrendingUpIcon />}
                      label={`TOP ${league.promotionPositions.length}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                  {league.demotionPositions?.length > 0 && (
                    <Chip
                      icon={<TrendingDownIcon />}
                      label={`BOTTOM ${league.demotionPositions.length}`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                  {hasRewards && (
                    <Chip
                      icon={<EmojiEventsIcon />}
                      label={`${
                        league.rewards?.filter((r) => r.rewardPack).length || 0
                      } Rewards`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={4}>
                {/* Reward Pack Selection */}
                <Grid item xs={12}>
                  <Card className={styles.rewardPackCard}>
                    <CardContent sx={{ p: "20px !important" }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          🏆 Reward Pack Configuration
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleAddReward(leagueName)}
                        >
                          Add Reward
                        </Button>
                      </Box>

                      {league.rewards && league.rewards.length > 0 ? (
                        league.rewards.map((reward, rewardIndex) => (
                          <Box
                            key={rewardIndex}
                            mb={2}
                            p={2}
                            sx={{
                              backgroundColor: "#f8f9fa",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="subtitle2" fontWeight={600}>
                                Reward {rewardIndex + 1}
                              </Typography>
                              {league.rewards.length > 1 && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleRemoveReward(leagueName, rewardIndex)
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={8}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Select Reward Pack</InputLabel>
                                  <Select
                                    value={reward.rewardPack || ""}
                                    onChange={(e) =>
                                      handleRewardPackChange(
                                        leagueName,
                                        rewardIndex,
                                        "rewardPack",
                                        e.target.value
                                      )
                                    }
                                    label="Select Reward Pack"
                                  >
                                    <MenuItem value="">
                                      <em>No reward pack</em>
                                    </MenuItem>
                                    {rewardPackOptions.map((pack) => (
                                      <MenuItem key={pack.id} value={pack.id}>
                                        {pack.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              {/* <Grid item xs={12} md={4}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Count"
                                  value={reward.rewardPackCount || 1}
                                  onChange={(e) =>
                                    handleRewardPackChange(
                                      leagueName,
                                      rewardIndex,
                                      "rewardPackCount",
                                      parseInt(e.target.value) || 1
                                    )
                                  }
                                  inputProps={{ min: 1, max: 100 }}
                                />
                              </Grid> */}
                            </Grid>

                            {reward.rewardPack && (
                              <Typography
                                variant="caption"
                                color="primary"
                                sx={{ mt: 1, display: "block" }}
                              >
                                ✓ Selected:{" "}
                                {
                                  rewardPackOptions.find(
                                    (pack) => pack.id === reward.rewardPack
                                  )?.label
                                }{" "}
                                (×{reward.rewardPackCount})
                              </Typography>
                            )}
                          </Box>
                        ))
                      ) : (
                        <Box textAlign="center" py={4}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            mb={2}
                          >
                            No rewards configured for this league
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddReward(leagueName)}
                          >
                            Add First Reward
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Position Configuration */}
                <Grid item xs={12} md={leagueName === "STARTER" ? 12 : 6}>
                  <Card className={styles.selectionCard}>
                    <CardContent>
                      {renderPositionSelector(leagueName, "promotion")}
                    </CardContent>
                  </Card>
                </Grid>

                {leagueName !== "STARTER" && (
                  <Grid item xs={12} md={6}>
                    <Card className={styles.selectionCard}>
                      <CardContent>
                        {renderPositionSelector(leagueName, "demotion")}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Box className={styles.helpText}>
        <Typography variant="body2" color="primary">
          <strong>🎯 Quick Guide:</strong>
          <br />• <strong>TOP 3</strong> means positions 1, 2, 3 get promoted
          <br />• <strong>BOTTOM 5</strong> means positions 16, 17, 18, 19, 20
          get demoted
          <br />• <strong>STARTER league</strong> has no demotion (lowest tier)
          <br />• Each league needs a reward pack for weekly rewards
          <br />• <strong>⚠️ Important:</strong> The same position cannot be
          used for both promotion and demotion
          <br />• <strong>Order:</strong> Determines league hierarchy (1 =
          lowest, 10 = highest)
        </Typography>
      </Box>

      {errors && errors.length > 0 && (
        <Box mt={2}>
          {errors.map((error, index) => (
            <Typography key={index} color="error" variant="body2">
              • {error}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LeagueConfigurationEditor;
